import { useEffect, useRef, useState } from "react"

interface InstagramEmbedProps {
  instagramUrl: string
}

export function InstagramEmbed({ instagramUrl }: InstagramEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const embedRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [embedReady, setEmbedReady] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: "200px" }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isVisible || scriptLoaded) return
    if (typeof window === "undefined") return

    if ((window as any).instgrm) {
      ;(window as any).instgrm.Embeds.process()
      setScriptLoaded(true)
      return
    }

    const script = document.createElement("script")
    script.src = "https://www.instagram.com/embed.js"
    script.async = true
    script.onload = () => {
      ;(window as any).instgrm?.Embeds.process()
      setScriptLoaded(true)
    }
    document.body.appendChild(script)
  }, [isVisible, scriptLoaded])

  // Watch for Instagram's iframe to appear and finish loading
  useEffect(() => {
    if (!isVisible || embedReady) return
    const container = embedRef.current
    if (!container) return

    const handleIframeLoad = (iframe: HTMLIFrameElement) => {
      iframe.addEventListener("load", () => setEmbedReady(true), {
        once: true,
      })
    }

    const observer = new MutationObserver(() => {
      const iframe = container.querySelector("iframe")
      if (iframe) {
        handleIframeLoad(iframe)
        observer.disconnect()
      }
    })

    observer.observe(container, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [isVisible, embedReady])

  return (
    <div
      ref={containerRef}
      className="mt-5 sm:mx-auto sm:w-full sm:max-w-sm"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "0.5rem",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: "450px",
          overflow: "hidden",
          border: "none",
          outline: "none",
          position: "relative",
        }}
      >
        {/* Loading skeleton - matches Instagram profile grid embed */}
        {!embedReady && (
          <div className="absolute inset-0 animate-pulse bg-card p-4">
            {/* Header: avatar + name/stats + IG icon */}
            <div className="flex items-center gap-3 mb-4">
              <div className="h-16 w-16 shrink-0 rounded-full bg-surface-strong" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 w-28 rounded bg-surface-strong" />
                <div className="h-3 w-20 rounded bg-surface-strong" />
                <div className="h-3 w-24 rounded bg-surface-strong" />
              </div>
              <div className="h-6 w-6 shrink-0 rounded bg-surface-strong self-start" />
            </div>
            {/* 3x2 photo grid */}
            <div className="grid grid-cols-3 gap-0.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-square bg-surface-strong" />
              ))}
            </div>
          </div>
        )}
        {isVisible && (
          <div
            ref={embedRef}
            className={`transition-opacity duration-300 ${embedReady ? "opacity-100" : "opacity-0"}`}
          >
            <style
              dangerouslySetInnerHTML={{
                __html: `.instagram-media, .instagram-media * { border: none !important; outline: none !important; box-shadow: none !important; } .instagram-media::before, .instagram-media::after, .instagram-media *::before, .instagram-media *::after { border: none !important; outline: none !important; }`,
              }}
            />
            <blockquote
              className="instagram-media"
              data-instgrm-permalink={instagramUrl}
              data-instgrm-version="12"
              style={{
                background: "#FFF",
                border: 0,
                borderRadius: "3px",
                boxShadow: "none",
                margin: "1px",
                maxWidth: "540px",
                minWidth: "326px",
                padding: 0,
                width: "calc(100% - 2px)",
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
